/**
 * useNotifications — SSE client hook for the notification inbox.
 *
 * Connects to /api/notifications/stream via native EventSource (SSE).
 * Manages notification state: list, unread count, mark-read, mark-all-read.
 *
 * Phase 20: surfaces lifecycle events, alerts (CD maturity, DQ), and
 * staff review queue items in real-time via the NotificationBell component.
 *
 * SSE connection pattern:
 *   EventSource /api/notifications/stream → pushes NotificationItem on each event
 *   The server (NotificationsController) emits one event per notification,
 *   keyed by {tenantId}:{staffId} room in Redis.
 *
 * AKS ingress requirement:
 *   NGINX must have proxy-buffering: off on the /api/notifications/stream path
 *   (see infra/modules/aks.bicep Phase 20 annotation block).
 */
import { useState, useEffect, useCallback } from 'react';

export interface NotificationItem {
  id: string;
  tenantId: string;
  assignedStaffId: string;
  notificationType: string;
  sourceEntityType: string | null;
  sourceEntityId: string | null;
  memberId: string | null;
  message: string;
  linkUrl: string | null;
  readAt: string | null;
  dismissedAt: string | null;
  createdAt: string;
}

interface UseNotificationsResult {
  notifications: NotificationItem[];
  unreadCount: number;
  isConnected: boolean;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  fetchNotifications: (page?: number, limit?: number) => Promise<void>;
}

export function useNotifications(): UseNotificationsResult {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);

  // SSE connection — connects to the notification stream on mount,
  // disconnects on unmount. New notifications are prepended to the list.
  useEffect(() => {
    const source = new EventSource('/api/notifications/stream', {
      withCredentials: true,
    });

    source.onopen = () => setIsConnected(true);

    source.onmessage = (event: MessageEvent) => {
      const notification: NotificationItem = JSON.parse(event.data as string);
      setNotifications((prev) => [notification, ...prev]);
      setUnreadCount((prev) => prev + 1);
    };

    source.onerror = () => setIsConnected(false);

    return () => source.close();
  }, []);

  // Fetch paginated notification history from REST endpoint.
  // Called on mount and after mark-all-read to sync server state.
  const fetchNotifications = useCallback(async (page = 1, limit = 20) => {
    const res = await fetch(`/api/notifications?page=${page}&limit=${limit}`, {
      credentials: 'include',
    });
    if (res.ok) {
      const data = (await res.json()) as {
        items: NotificationItem[];
        unreadCount: number;
      };
      setNotifications(data.items);
      setUnreadCount(data.unreadCount);
    }
  }, []);

  // Initial fetch — loads existing notifications on mount
  useEffect(() => {
    void fetchNotifications();
  }, [fetchNotifications]);

  // Mark a single notification as read (optimistic update + server PATCH)
  const markRead = useCallback(async (id: string) => {
    await fetch(`/api/notifications/${id}/read`, {
      method: 'PATCH',
      credentials: 'include',
    });
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, readAt: new Date().toISOString() } : n)),
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));
  }, []);

  // Mark all notifications as read (optimistic update + server PATCH)
  const markAllRead = useCallback(async () => {
    await fetch('/api/notifications/read-all', {
      method: 'PATCH',
      credentials: 'include',
    });
    setNotifications((prev) =>
      prev.map((n) => ({ ...n, readAt: n.readAt ?? new Date().toISOString() })),
    );
    setUnreadCount(0);
  }, []);

  return { notifications, unreadCount, isConnected, markRead, markAllRead, fetchNotifications };
}
